import { SectionBox } from '@shared/components/ui/SectionBox';
import { faCalendar, faDollarSign, faFileInvoice, faTag } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateShortMonthEs } from '@shared/utils/FormatUtils';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { Invoice } from '../../models/InvoicesView';

interface InvoiceHeaderProps {
  invoice: Invoice;
  isFetching?: boolean;
}

// Función utilitaria para formatear montos
const formatAmount = (amount: number | undefined | null, currency: string): string =>
  formatCurrencyAmount(amount ?? 0, currency || 'EUR');

// Función para traducir el período
const getPeriodLabel = (period: string | undefined | null, t: (key: string) => string): string => {
  if (!period) return '-';
  const periodMap: Record<string, string> = {
    DAILY: t('filters.periodOptions.daily', 'Diario'),
    WEEKLY: t('filters.periodOptions.weekly', 'Semanal'),
    MONTHLY: t('filters.periodOptions.monthly', 'Mensual'),
    QUARTERLY: t('filters.periodOptions.quarterly', 'Trimestral'),
    ANNUAL: t('filters.periodOptions.annual', 'Anual'),
  };
  return periodMap[period.toUpperCase()] || period;
};

// Función para traducir el tipo
const getTypeLabel = (type: string | undefined, t: (key: string) => string): string => {
  if (!type) return '-';
  const typeMap: Record<string, string> = {
    SHIPPING: t('table.types.shipping', 'Envío'),
    RETURN: t('table.types.return', 'Devolución'),
    RESHIPMENT: t('table.types.reshipment', 'Reenvío'),
    STORAGE: t('table.type_storage', 'Almacenamiento'),
  };
  return typeMap[type.toUpperCase()] || type;
};

export const InvoiceHeader = ({ invoice, isFetching = false }: InvoiceHeaderProps) => {
  const { t } = useTranslation('invoicesViewModule');
  const currency = useCurrency();

  const formattedDate = invoice.date ? formatDateShortMonthEs(invoice.date) : '-';
  const periodLabel = getPeriodLabel(invoice.period, t);
  const typeLabel = getTypeLabel(invoice.type, t);

  // Usar erp_number como prioridad, luego code o id como fallback
  const invoiceCode = invoice.erp_number ?? invoice.code ?? (invoice.id ? `#${invoice.id}` : '-');

  const cards = [
    {
      title: t('header.code', 'Código'),
      value: invoiceCode,
      icon: faFileInvoice,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.date', 'Fecha'),
      value: formattedDate,
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
  ];

  // Si hay tipo, agregar card de tipo
  if (invoice.type) {
    cards.push({
      title: t('header.type', 'Tipo'),
      value: typeLabel,
      icon: faTag,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    });
  }

  // Si hay período, agregar card de período
  if (invoice.period) {
    cards.push({
      title: t('header.period', 'Período'),
      value: periodLabel,
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    });
  }

  if (invoice.grand_total !== undefined) {
    cards.push({
      title: t('header.grand_total', 'Total'),
      value: formatAmount(invoice.grand_total, currency),
      icon: faDollarSign,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    });
  }

  const totalCards = cards.length;
  const gridColsClass =
    totalCards === 5
      ? 'md:grid-cols-5'
      : totalCards === 6
        ? 'md:grid-cols-6'
        : totalCards === 7
          ? 'md:grid-cols-7'
          : totalCards === 8
            ? 'md:grid-cols-8'
            : 'md:grid-cols-4';

  return (
    <div className="mb-6">
      <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
        {cards.map((card, index) => (
          <SectionBox key={index} className="">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm text-subtext mb-1">{card.title}</h3>
                <p className={`text-md text-title ${card.valueClassName || ''}`}>
                  {isFetching ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-pulse">...</span>
                    </span>
                  ) : (
                    card.value
                  )}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${card.iconBgClass}`}>
                <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${card.iconTextClass}`} />
              </div>
            </div>
          </SectionBox>
        ))}
      </div>
    </div>
  );
};
